import { endOfMonth, format, formatDistanceToNow, startOfMonth } from 'date-fns'
import { enUS, de, cs, es, fr, nl, ru, sk } from 'date-fns/locale'

type FormatType = 'distance' | 'absolute' | 'withTime' | 'auto' | 'hours' | 'withoutYear'

const twelveHourLocales = ['en'] // Only English uses 12-hour among your list

const localeDateFormatMap: Record<string, string> = {
  en: 'MMM dd, yyyy',
  de: 'dd MMM yyyy',
  cs: 'dd. MM. yyyy',
  es: 'dd MMM yyyy',
  fr: 'dd MMM yyyy',
  nl: 'dd MMM yyyy',
  ru: 'dd.MM.yyyy',
  sk: 'dd.MM.yyyy'
}

const localeDateFormatMapWithoutYear: Record<string, string> = {
  en: 'MMM dd',
  de: 'dd MMM',
  cs: 'dd. MM.',
  es: 'dd MMM',
  fr: 'dd MMM',
  nl: 'dd MMM',
  ru: 'dd.MM.',
  sk: 'dd.MM.'
}

export const dateFnsLocales: { [key: string]: Locale } = {
  en: enUS,
  de: de,
  cs: cs,
  es: es,
  fr: fr,
  nl: nl,
  ru: ru,
  sk: sk
}

const formatDateWithoutYearWithDay = (date: Date, language: string, timeZone: string) => {
  const parts = new Intl.DateTimeFormat(language, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    timeZone
  }).formatToParts(date)

  const weekday = parts.find(p => p.type === 'weekday')?.value
  const day = parts.find(p => p.type === 'day')?.value
  const month = parts.find(p => p.type === 'month')?.value

  const capitalize = (str?: string) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : '')

  return `${capitalize(weekday)}, ${capitalize(month)} ${day}`
}

const uses12HourClock = (localeCode: string): boolean => {
  return twelveHourLocales.includes(localeCode)
}

export const getDateFnsLocale = (i18nLanguage: string) => dateFnsLocales[i18nLanguage] || enUS

export const getFormatedTime = (
  timeStamp: number,
  language: string,
  formatType: FormatType = 'auto',
  timeZone?: string
) => {
  const locale = getDateFnsLocale(language)

  const date = new Date(timeStamp)
  const startedAtDate = new Date(timeStamp)
  const now = new Date()
  const hoursDifference = (now.getTime() - startedAtDate.getTime()) / (1000 * 60 * 60) // Convert ms to hours

  const hourType = uses12HourClock(language) ? 'h:mm a' : 'HH:mm'

  const datePattern = localeDateFormatMap[language] || 'MMM dd, yyyy'
  const datePatternWithoutYear = localeDateFormatMapWithoutYear[language] || 'MMM dd'

  const formatMap: Record<FormatType, string> = {
    distance: '', // handled differently
    absolute: datePattern,
    withTime: `${datePattern} ${hourType}`,
    auto: hoursDifference < 24 ? '' : datePattern,
    hours: hourType,
    withoutYear: datePatternWithoutYear
  }

  const formatPattern = formatMap[formatType]

  // 🟡 TIMEZONE-SAFE fallback
  if (timeZone && (formatType === 'hours' || formatType === 'withTime' || formatType === 'withoutYear')) {
    const time = date.toLocaleTimeString(language, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: uses12HourClock(language),
      timeZone
    })

    if (formatType === 'hours') return time

    if (formatType === 'withoutYear') return formatDateWithoutYearWithDay(date, language, timeZone)
  }

  if (formatType === 'distance' || (formatType === 'auto' && hoursDifference < 24)) {
    return `${formatDistanceToNow(startedAtDate, { addSuffix: true, locale })}`
  }

  let formattedDate = format(startedAtDate, formatPattern, { locale })

  if (formatType === 'withTime' && language === 'de') {
    return (formattedDate += ' Uhr')
  }

  return formattedDate
}

export const convertLocalTimestampToUTC = (localTimestamp: number) => {
  const localDate = new Date(localTimestamp)
  const timezoneOffset = localDate.getTimezoneOffset() * 60 * 1000

  return localTimestamp - timezoneOffset
}

export const getStartOfMonthUTC = (date: Date) => {
  const startLocal = startOfMonth(date).getTime()

  return convertLocalTimestampToUTC(startLocal)
}

export const getEndOfMonthUTC = (date: Date) => {
  const endLocal = endOfMonth(date).getTime()

  return convertLocalTimestampToUTC(endLocal)
}
